import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Language, LanguageContextType } from '../types/language';

// Import all translations
import { homeTranslations } from '../translations/home';
import { authTranslations } from '../translations/auth';
import { chatTranslations } from '../translations/chat';
import { commonTranslations } from '../translations/common';
import { teamTranslations } from '../translations/team';
import { dashboardTranslations } from '../translations/dashboard';
import { singleModuleTranslations } from '../translations/singleModule';
import { fullProgramTranslations } from '../translations/fullProgram';
import { aiAgentsTranslations } from '../translations/aiAgents';
import { nextStepsTranslations } from '../translations/nextSteps';
import { uiBasicsTranslations } from '../translations/uiBasics';
import { webScrapingTranslations } from '../translations/webScraping';
import { vibecodingTranslations } from '../translations/vibeCoding';

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  // Translation function
  const t = (key: string, page?: string): string => {
    let translations;
    
    switch (page) {
      case 'home':
        translations = homeTranslations[language];
        break;
      case 'auth':
        translations = authTranslations[language];
        break;
      case 'chat':
        translations = chatTranslations[language];
        break;
      case 'team':
        translations = teamTranslations[language];
        break;
      case 'dashboard':
        translations = dashboardTranslations[language];
        break;
      case 'singleModule':
        translations = singleModuleTranslations[language];
        break;
      case 'fullProgram':
        translations = fullProgramTranslations[language];
        break;
      case 'aiAgents':
        translations = aiAgentsTranslations[language];
        break;
      case 'nextSteps':
        translations = nextStepsTranslations[language];
        break;
      case 'uiBasics':
        translations = uiBasicsTranslations[language];
        break;
      case 'webScraping':
        translations = webScrapingTranslations[language];
        break;
      case 'vibeCoding':
        translations = vibecodingTranslations[language];
        break;
      case 'common':
      default:
        translations = commonTranslations[language];
        break;
    }

    // Support nested keys like "hero.title"
    const keys = key.split('.');
    let value: any = translations;
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return value || key; // Return key if translation not found
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};